import { ITag } from 'src/tag/tag.models';
import { IMasterScheduleBase } from '@masterSchedule/masterSchedule.models';
import { IProjectCompanyOption } from '@company/projectCompany/projectCompany.models';
import { IRiskCategory } from '@riskCategory/riskCategory.models';
import { FieldValues } from '@gcl/react-core';
import { IProjectUserProfileWithTeamOption } from '@userProfile/userProfile.models';
import { IVoItem } from '@voItem/voItem.models';
import { IApiBaseProps } from '@common/api.models';
import { ITempProjectFile } from '@file/projectFile/projectFile.models';

export enum EMasterScheduleFormKey {
  REF_NO = 'refNo',
  DESIGN_PACKAGE = 'designPackage',
  RISK_CATEGORY = 'riskCategory',
  VO_ITEM = 'voItem',
  REQUIRED_WORK_PROCEDURE_DRAWINGS = 'requiredWorkProcedureDrawings',
  PROCUREMENT_DATE = 'procurementDate',
  START_CONSTRUCTION_DATE = 'startConstructionDate',
  REMOVAL_DATE = 'removalDate',
  REMARKS = 'remarks',
  HAS_NO_DESIGNER = 'hasNoDesigner',
  REMARK_FOR_NO_DESIGNER = 'remarkForNoDesigner',
  RESPONSIBLE_DESIGNER = 'responsibleDesigner',
  RESPONSIBLE_CHECKER = 'responsibleChecker',
  RESPONSIBLE_REVIEWER = 'responsibleReviewer',
  RESPONSIBLE_SRP = 'responsibleSrp',
  RESPONSIBLE_TWC = 'responsibleTwc',
  RESPONSIBLE_TWCM = 'responsibleTwcm',
  RESPONSIBLE_SITE_OPERATION_TEAM = 'responsibleSiteOperationTeams',
  RESPONSIBLE_DESIGNER_TEAM = 'responsibleDesignerTeams',
  RESPONSIBLE_CHECKER_TEAM = 'responsibleCheckerTeams',
  RESPONSIBLE_REVIEWER_TEAM = 'responsibleReviewerTeams',
  CREATED_BY = 'createdBy.displayName',
  FALSEWORK = 'isFalsework',
  SUPPLEMENTARY_FILES = 'supplementaryFiles',
}

export enum EMasterScheduleConfirmationActionType {
  CANCEL = 'cancel',
  UNCANCEL = 'uncancel',
  LOCK = 'lock',
  UNLOCK = 'unlock',
}

export interface IMasterScheduleFormState {
  [EMasterScheduleFormKey.REF_NO]: string | null;
  [EMasterScheduleFormKey.DESIGN_PACKAGE]: string | null;
  [EMasterScheduleFormKey.RISK_CATEGORY]: IRiskCategory | null;
  [EMasterScheduleFormKey.VO_ITEM]: IVoItem | null;
  [EMasterScheduleFormKey.REQUIRED_WORK_PROCEDURE_DRAWINGS]: boolean | null;
  [EMasterScheduleFormKey.PROCUREMENT_DATE]: string | null;
  [EMasterScheduleFormKey.START_CONSTRUCTION_DATE]: string | null;
  [EMasterScheduleFormKey.REMOVAL_DATE]: string | null;
  [EMasterScheduleFormKey.REMARKS]: string | null;
  [EMasterScheduleFormKey.HAS_NO_DESIGNER]: boolean | null;
  [EMasterScheduleFormKey.REMARK_FOR_NO_DESIGNER]: string | null;
  [EMasterScheduleFormKey.RESPONSIBLE_DESIGNER]: IProjectCompanyOption | null;
  [EMasterScheduleFormKey.RESPONSIBLE_CHECKER]: IProjectCompanyOption | null;
  [EMasterScheduleFormKey.RESPONSIBLE_REVIEWER]: IProjectCompanyOption | null;
  [EMasterScheduleFormKey.RESPONSIBLE_SRP]: IProjectUserProfileWithTeamOption[];
  [EMasterScheduleFormKey.RESPONSIBLE_TWC]: IProjectUserProfileWithTeamOption[];
  [EMasterScheduleFormKey.RESPONSIBLE_TWCM]: IProjectUserProfileWithTeamOption[];
  [EMasterScheduleFormKey.RESPONSIBLE_SITE_OPERATION_TEAM]: IResponsibleTeam[];
  [EMasterScheduleFormKey.RESPONSIBLE_DESIGNER_TEAM]: IResponsibleTeam[];
  [EMasterScheduleFormKey.RESPONSIBLE_CHECKER_TEAM]: IResponsibleTeam[];
  [EMasterScheduleFormKey.RESPONSIBLE_REVIEWER_TEAM]: IResponsibleTeam[];
  [EMasterScheduleFormKey.FALSEWORK]: boolean | null;
  [EMasterScheduleFormKey.SUPPLEMENTARY_FILES]: ITempProjectFile[];
}
export interface ICreateT1Request extends IUpsertT1Request {}

export interface IUpdateT1Request extends IUpsertT1Request {
  rowVersion: string;
}

export interface IUpsertT1Request {
  projectId: number;
  refNo?: string;
  designPackage?: string;
  riskCategory: IRiskCategory;
  voItem: IVoItem;
  isFalsework: boolean;
  requiredWorkProcedureDrawings?: boolean;
  procurementDate?: string;
  startConstructionDate?: string;
  removalDate?: string;
  remarks?: string;
  responsibleDesigner?: IProjectCompanyOption;
  responsibleChecker?: IProjectCompanyOption;
  responsibleReviewer?: IProjectCompanyOption;
  responsibleDesignerTeams?: IUpsertT1ResponsibleProjectTeam[];
  responsibleCheckerTeams?: IUpsertT1ResponsibleProjectTeam[];
  responsibleReviewerTeams?: IUpsertT1ResponsibleProjectTeam[];
  responsibleSiteOperationTeams?: IUpsertT1ResponsibleProjectTeam[];
  responsibleSrp?: IUpsertT1ResponsibleUser[];
  responsibleTwc?: IUpsertT1ResponsibleUser[];
  responsibleTwcm?: IUpsertT1ResponsibleUser[];
  tags?: IUpsertT1TagRequest[];
}
export interface IUpsertT1ResponsibleProjectTeam {
  id?: number;
  projectId: number;
  projectMasterScheduleId?: number;
  projectTeamId: number;
  rowVersion?: string;
}
export interface IUpsertT1ResponsibleUser {
  id?: number;
  projectId: number;
  projectUserProfileId: number;
  projectMasterScheduleId?: number;
  rowVersion: string;
}

export interface IMasterScheduleDetailForm
  extends Omit<
      IUpsertT1Request,
      'tags' | 'responsibleSrp' | 'responsibleTwc' | 'responsibleTwcm' | 'responsibleDesignerTeams'
    >,
    FieldValues {
  responsibleSrp?: IProjectUserProfileWithTeamOption[];
  responsibleTwc?: IProjectUserProfileWithTeamOption[];
  responsibleTwcm?: IProjectUserProfileWithTeamOption[];
  responsibleDesignerTeams?: IResponsibleTeam[];
  responsibleCheckerTeams?: IResponsibleTeam[];
  responsibleReviewerTeams?: IResponsibleTeam[];
  responsibleSiteOperationTeams?: IResponsibleTeam[];
}

export interface IUpsertT1TagRequest {
  id?: number;
  projectMasterScheduleId?: number;
  tagTypeId: number;
  name: string;
}

export interface IMasterScheduleDetail extends IMasterScheduleBase {
  projectId: number;
  voItem: IVoItem;
  requiredWorkProcedureDrawings: boolean;
  procurementDate?: string;
  startConstructionDate?: string;
  removalDate?: string;
  remarks: string;
  hasNoDesigner: boolean;
  remarkForNoDesigner: string;
  responsibleDesigner: IProjectCompanyOption;
  responsibleChecker: IProjectCompanyOption;
  responsibleReviewer: IProjectCompanyOption;
  responsibleDesignerTeams: IResponsibleTeam[];
  responsibleCheckerTeams: IResponsibleTeam[];
  responsibleReviewerTeams: IResponsibleTeam[];
  responsibleSiteOperationTeams: IResponsibleTeam[];
  responsibleTwc?: IResponsibleUser[];
  responsibleTwcm?: IResponsibleUser[];
  responsibleSrp?: IResponsibleUser[];
  isFalsework: boolean;
  canCancel: boolean;
  isCancelled: boolean;
  isLocked: boolean;
  isDeleted?: boolean;
  tags?: ITag[];
  rowVersion: string;
}

export interface IResponsibleUser {
  id: number;
  projectId: number;
  projectMasterScheduleId: number;
  projectUserProfileId: number;
  removed: boolean;
  rowVersion: string;
  uid: string;
  upn: string;
  email: string;
  displayName: string;
}

export interface IResponsibleTeam {
  id: number;
  projectMasterScheduleId: number;
  projectTeamId: number;
  projectId: number;
  rowVersion?: string;
  teamName?: string;
}

export interface ICancelProjectMasterScheduleRequest extends IApiBaseProps {
  id: number;
}

export interface ILockProjectMasterScheduleRequest extends IApiBaseProps {
  id: number;
}
